import { TwinParameters } from '@teinor/erp/types/company/config/parameter'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../baseComponents/Layout/SingleFormLayout'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { GroupVatPayload } from '../../../../../../../utils/reducers/globals/groupVats'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import useCustomerTypesDict from '../../../../../../../utils/hooks/customer/useCustomerTypesDict'
import useOrderTypeSelectProductsDict from '../../../../../../../utils/hooks/orders/useOrderTypeSelectProductsDict'
import { TextForOrderParameter } from './Subcomponents'

type OrderParametersProps = ComponentWithPermissions & ReduxOrderParameters & {
    basicData: TwinParameters
    getData: () => Promise<void>
}

interface TwinParametersCustom extends TwinParameters {
    textForRef: string
}

const OrderParameters: React.FC<OrderParametersProps> = ({ basicData, getData, userPermissions, groupVats }) => {
    const { t } = useTwinTranslation()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const onOffEraser = {
        'off': { name: t('eraser', 'Borrador') },
        'on': { name: t('finalDocument', 'Documento final') },
    }
    const onOffDraftOrPending = {
        'off': { name: t('eraser', 'Borrador') },
        'on': { name: t('pending', 'Pendiente') },
    }
    const orderTypeSelectProductsDict = useOrderTypeSelectProductsDict()
    const defaultViewWorksheet = {
        'default': { name: t('list', 'Lista') },
        'product_status_view': { name: t('board', 'Tablero')}
    }
    const { dictCustomerTypeFilter } = useCustomerTypesDict()
    const fields: FormRender<TwinParametersCustom> = [
        {
            cols: 3,
            title: t('quoteParameters', 'Parámetros de presupuestos'),
            elements: [
                {
                    name: 'quoteShippingMandatory',
                    label: t('quoteShippingMandatoryDefault', 'Transportista obligatorio en el presupuesto por defecto'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'quoteOrderDefaultCustomerType',
                    label: t('quoteOrderDefaultCustomerType', 'Tipo de cliente por defecto en el presupuesto'),
                    component: 'CustomSelect',
                    items: dictCustomerTypeFilter
                },
            ]
        },
        {
            cols: 3,
            title: t('buyParameters', 'Parámetros de Compra'),
            elements: [
                {
                    name: 'introduceLastBuyPriceAuto',
                    label: t('introduceLastPriceAuto', 'Introducir último precio automáticamente'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'introduceLastBuyDiscountAuto',
                    label: t('introduceLastDiscountAuto', 'Introducir último descuento automáticamente'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'updateSupplierPrice',
                    label: t('updateSupplierPriceLong', 'Actualizar el precio del Proveedor'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'updateCostPriceAndMargin',
                    label: t('updateCostPriceLong', 'Actualizar el precio de coste'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'calculateNextDeliveryDateFromLastBuy',
                    label: t('calculateDeliveryDateFromLastBuy', 'Calcular fecha de entrega en base a la útlima compra '),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'buyShippingGroupVatId',
                    label: t('defaultBuyShippingGroupVat', 'Grupo de impuestos envío compras por defecto'),
                    component: 'CustomSelect',
                    items: groupVats || {}
                },
                {
                    name: 'buyToDeliveryNoteFinished',
                    label: t('passingBuyToDeliveryNoteParam', 'Pasar de pedido a albarán como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                {
                    name: 'buyToInvoiceFinished',
                    label: t('passingBuyToInvoiceParam', 'Pasar de pedido a factura como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                {
                    name: 'buyDeliveryNoteToInvoiceFinished',
                    label: t('passingDeliveryNoteToInvoiceParam', 'Pasar de albarán a factura como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                // {
                //     name: 'refundDeliveryNoteFinished',
                //     label: t('refundDeliveryNoteBeAuto', 'Las devoluciones de albarán serán automáticas'),
                //     component: 'CustomSelect',
                //     items: onOff
                // },
                // {
                //     name: 'refundInvoiceFinished',
                //     label: t('refundInvoiceBeAuto', 'Las devoluciones de facturas serán automáticas'),
                //     component: 'CustomSelect',
                //     items: onOff
                // },
                {
                    name: 'buyShippingMandatory',
                    label: t('buyShippingMandatoryDefault', 'Transportista obligatorio en compras por defecto'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'buyAddAutoAddons',
                    label: t('buyAddAutoAddonsDefault', 'Añadir complementos en compras por defecto'),
                    component: 'CustomSelect',
                    items: onOff
                },
            ]
        },
        {
            cols: 3,
            title: t('sellOrderParameters', 'Parámetros de pedidos de venta'),
            elements: [
                {
                    name: 'quoteToSellOrderFinished',
                    label: t('passingQuoteOrderToSellOrderParam', 'Pasar de presupuesto a pedido como'),
                    component: 'CustomSelect',
                    items: onOffDraftOrPending
                },
                {
                    name: 'quoteToDeliveryNoteFinished',
                    label: t('passingQuoteOrderToDeliveryNoteParam', 'Pasar de presupuesto a albarán como'),
                    component: 'CustomSelect',
                    items: onOffDraftOrPending
                },
                {
                    name: 'quoteToInvoiceFinished',
                    label: t('passingQuoteOrderToSellInvoiceParam', 'Pasar de presupuesto a factura como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                {
                    name: 'sellToDeliveryNoteFinished',
                    label: t('passingSellOrderToDeliveryNoteParam', 'Pasar de pedido a albarán como'),
                    component: 'CustomSelect',
                    items: onOffDraftOrPending
                },
                {
                    name: 'sellToInvoiceFinished',
                    label: t('passingSellToInvoiceParam', 'Pasar de pedido a factura como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                {
                    name: 'sellDeliveryNoteToInvoiceFinished',
                    label: t('passingDeliveryNoteToInvoiceParam', 'Pasar de albarán a factura como'),
                    component: 'CustomSelect',
                    items: onOffEraser
                },
                {
                    name: 'sellInvoicePaymentMethodMandatory',
                    label: t('sellInvoicePaymentMethodMandatory', 'Método de pago obligatorio en el factura de venta'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'workSheetTypeByDefault',
                    label: t('workSheetTypeByDefault', 'Tipo de hoja de trabajo por defecto'),
                    component: 'CustomSelect',
                    items: orderTypeSelectProductsDict
                },
                {
                    name: 'createWorkSheetSellOrderByDefault',
                    label: t('createWorkSheetWhenProcessingSellOrders', 'Crear hoja de trabajo al procesar pedidos'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'createWorkSheetSellDeliveryNoteByDefault',
                    label: t('createWorkSheetWhenProcessingDeliveryNotes', 'Crear hoja de trabajo al procesar albaranes'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'createWorkSheetSellInvoiceByDefault',
                    label: t('createWorkSheetWhenProcessingInvoices', 'Crear hoja de trabajo al procesar facturas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'defaultEnterWorksheetView',
                    label: t('defaultEnterWorksheetView', 'Vista por defecto hoja de trabajo'),
                    component: 'CustomSelect',
                    items: defaultViewWorksheet
                },
                {
                    name: 'showImageWorkSheetCard',
                    label: t('showImageWorkSheetCard', 'Mostrar imagen en la hoja de trabajo'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'showDocDescriptionWorkSheetCard',
                    label: t('showDocDescriptionWorkSheetCard', 'Mostrar descripción de documento en la hoja de trabajo'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'showNotesWorkSheetCard',
                    label: t('showNotesWorkSheetCard', 'Mostrar notas en la hoja de trabajo'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'typePrintProducts',
                    label: t('typePrintProducts', 'Tipo de impresión productos'),
                    component: 'CustomSelect',
                    items: orderTypeSelectProductsDict
                },
                {
                    name: 'maxRefundDays',
                    label: t('maxRefundDays', 'Plazo máximo de devolución (en días)'),
                    component: 'InputWithLabelMargin',
                },
                {
                    name: 'activeCreateProductFromTPV',
                    label: t('activeCreateProductFromTPV', 'Activar poder crear producto desde TPV'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'voucherProductId',
                    label: t('voucherProductId', 'Producto vale ID'),
                    component: 'InputWithLabelMargin',
                },
            ]
        },
        {
            cols: 3,
            title: t('parameters', 'Parámetros'),
            elements: [
                {
                    name: 'orderAddAddonsUnitFooterTotal',
                    label: t('orderAddAddonsUnitFooterTotal', 'Sumar las unidades de los complementos al total'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'orderAddAddonsWeightFooterTotal',
                    label: t('orderAddAddonsWeightFooterTotal', 'Sumar el peso de los complementos al total'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'assignRepresentativeFromCustomerInSells',
                    label: t('assignRepresentativeFromCustomerInSells', 'Asignar representante desde cliente en ventas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'assignSpecificPriceRuleFromCustomerInSells',
                    label: t('customerDefinesZonePricesInSells', 'El cliente define la zona de precio en ventas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'assignVatFromCustomerInSells',
                    label: t('customerDefinesZoneVatInSells', 'El cliente define la zona de iva en ventas'),
                    component: 'CustomSelect',
                    items: onOff
                },
            ]
        },
        {
            cols: 3,
            title: t('orderGenerationParameters', 'Parámetros de nombres automáticos de pedidos'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: TextForOrderParameter
                },
                {
                    name: 'sellTicketPartialChargedText',
                    label: t('sellTicketPartialChargedText', 'Texto para tickets cobrados parcialmente'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    defaultValue: basicData?.sellTicketPartialChargedText || ''
                },
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)
    return (
        <SingleFormLayout action='/api/app/config/parameter/updateParameters' onSubmit={getData} sections={parsedFields} userPermissions={userPermissions} />
    )
}

const orderParametersDispatch = {
    setGroupVats: (payload: GroupVatPayload) => ({ type: 'CHANGE_GROUPVAT', payload }),
}

export type ReduxOrderParameters = ConnectedProps<typeof orderParametersConnect>
const mapOrderParametersConnector = (state: AllReduxPayloads) => ({ groupVats: state.groupVats })
const orderParametersConnect = connect(mapOrderParametersConnector, orderParametersDispatch)

const orderParametersConnectLoading = withLoading(OrderParameters, [{ fetchUrl: '/api/app/config/vat/getAllGroupsVat', propName: 'groupVats', setFunctionName: 'setGroupVats' }])

const OrderParametersConnect = orderParametersConnect(orderParametersConnectLoading)

export default OrderParametersConnect